import axios from 'axios';
import { toast } from 'react-toastify';
import { storage as secureStorage } from '../utils/helpers';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 20000, // 20 seconds timeout
  withCredentials: true, // Enable cookies for cross-origin requests
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': 'web', // Identify as web client for cookie-based auth
    // 'Cache-Control': 'no-cache, no-store, must-revalidate',
    // 'Pragma': 'no-cache',
    // 'Expires': '0',
  },
});

// Single shared promise for token refresh
// all concurrent 401s await the same refresh call.
let refreshPromise = null;

const removeUserData = () => {
  secureStorage.clear();
};

const handleSessionExpired = () => {
  removeUserData();
  window.location.href = '/login';
  toast.error('Session expired. Please login again.');
};

const refreshAccessToken = () => {
  console.log('Refreshing access token...');

  return axios
    .post(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/refresh`,
      {},
      { 
        withCredentials: true, // Include cookies (refresh token)
        headers: { 'X-Client-Type': 'web' }
      }
    )
    .then((res) => {
      const { access_token } = res.data;
      secureStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
      return access_token;
    })
    .catch((err) => {
      handleSessionExpired();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = secureStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle specific error codes
    if (error.response?.status === 401) {
      // reject login or refresh requests themselves
      if (originalRequest?.url?.includes('/auth/login') ||
          originalRequest?.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true;

        // Reuse existing refresh promise for single refresh attempt across multiple 401 errors
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken();
        }

        try {
          const newToken = await refreshPromise;
          originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
          return api(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    } else if (error.response?.status === 403) {
      // Handle permission errors with more context
      const isAdminRoute = error.config?.url?.includes('/admin/');
      const isUserManagement = error.config?.url?.includes('/users');
      
      if (isAdminRoute || isUserManagement) {
        toast.error('Admin access required. You do not have permission to access this resource.');
      } else {
        toast.error('Access denied. You do not have permission to perform this action.');
      }
    } else if (error.response?.status === 404) {
      toast.error('Resource not found.');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    }
    
    return Promise.reject(error);
  }
);

export default api;
